import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import {
  listMantenimientoDetalles,
  updateDetalleNumeros,
  type MantenimientoDetalleRow,
} from '../api';

/**
 * Per-line NroSerie + NroDespacho editor. Mirrors PowerApps
 * `PopUp_DespachoStage1` — the admin inputs serial numbers and
 * dispatch numbers per articulo from the physical paperwork before
 * hitting "Confirmar N° Despacho".
 *
 * UI pattern: view mode lists all lines; the pencil icon enters
 * inline edit state for a single line, and saving it patches the
 * detalle via `PUT /detalles/:detalleId/numeros`.
 */
export function NumerosSerieDespachoDialog({
  ventaId,
  onClose,
  disabled,
  disabledReason,
  nroDespachoConfirmado = false,
}: {
  ventaId: string;
  onClose: () => void;
  /** When Status_RO='Anulado' or the despacho is already filed we
   *  render read-only — those numbers are part of a frozen record. */
  disabled?: boolean;
  /** Optional human-readable reason rendered as a banner at the top
   *  of the dialog when `disabled` is true. */
  disabledReason?: string;
  /** True when the admin has already pressed "Confirmar N° Despacho"
   *  on this venta — editing a serial / despacho number from here
   *  invalidates that confirmation, so we surface a warning + ask
   *  the admin to acknowledge before the patch lands. */
  nroDespachoConfirmado?: boolean;
}) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const detallesQ = useQuery({
    queryKey: ['mantenimiento', 'detalles', ventaId],
    queryFn: () => listMantenimientoDetalles(ventaId),
    staleTime: 30_000,
  });
  const rows = useMemo(() => detallesQ.data ?? [], [detallesQ.data]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Números de Serie / Números de Despacho</DialogTitle>
          <DialogDescription>Venta Nº {ventaId}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {disabled && disabledReason && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {disabledReason}
            </div>
          )}
          {detallesQ.isPending ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : detallesQ.isError ? (
            <div className="py-6 text-center text-sm text-destructive">
              No se pudo cargar el detalle.
            </div>
          ) : rows.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Sin líneas activas.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <Th>Artículo</Th>
                    <Th>Num. de Serie</Th>
                    <Th>Num. de Despacho</Th>
                    <Th className="w-16 text-right">Acciones</Th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((row) =>
                    editingId === row.id ? (
                      <EditRow
                        key={row.id}
                        row={row}
                        onCancel={() => setEditingId(null)}
                        onSaved={(resetConfirmacion) => {
                          setEditingId(null);
                          // Prefix invalidate every `mantenimiento.detalles`
                          // cache so both this dialog (key:
                          // `[..., ventaId]`) and the parent page (key:
                          // `[..., countryCode, id]`) refetch. Without
                          // the broad prefix, the parent's
                          // `articulosFaltantes` stays stale and the
                          // "Datos faltantes" guard fires after a save.
                          qc.invalidateQueries({
                            queryKey: ['mantenimiento', 'detalles'],
                          });
                          // Reset the venta-level header cache so the
                          // "Confirmar N° Despacho" pill flips back
                          // to "action" without a manual refresh.
                          if (resetConfirmacion) {
                            qc.invalidateQueries({ queryKey: ['mantenimiento'] });
                          }
                        }}
                        ventaId={ventaId}
                        requireReconfirmation={nroDespachoConfirmado}
                      />
                    ) : (
                      <ViewRow
                        key={row.id}
                        row={row}
                        disabled={disabled}
                        onEdit={() => setEditingId(row.id)}
                      />
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            className="w-full bg-foreground text-background hover:bg-foreground/90"
            onClick={onClose}
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewRow({
  row,
  disabled,
  onEdit,
}: {
  row: MantenimientoDetalleRow;
  disabled?: boolean;
  onEdit: () => void;
}) {
  // Non-serie lines (cuenta contable says NroSerie=NO, e.g. Accesorios,
  // Repuestos): render "—" for both columns and disable the edit
  // pencil. There's nothing for the admin to type — the missing-
  // numeros gate also skips these so they don't block confirmar
  // despacho.
  const noSerie = !row.llevaNroSerie;
  const editDisabled = disabled || noSerie;
  return (
    <tr>
      <Td className="font-semibold text-foreground">{row.articulo}</Td>
      <Td className={cn(noSerie || !row.nroSerie ? 'text-muted-foreground' : '')}>
        {noSerie ? '—' : row.nroSerie || 'Pendiente'}
      </Td>
      <Td className={cn(noSerie || !row.nroDespacho ? 'text-muted-foreground' : '')}>
        {noSerie ? '—' : row.nroDespacho || 'Pendiente'}
      </Td>
      <Td className="text-right">
        <button
          type="button"
          disabled={editDisabled}
          onClick={onEdit}
          title={
            noSerie
              ? 'Esta cuenta contable no requiere Nº de Serie'
              : 'Editar números'
          }
          aria-label="Editar números"
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
            editDisabled && 'pointer-events-none opacity-50',
          )}
        >
          <Pencil className="h-4 w-4" aria-hidden />
        </button>
      </Td>
    </tr>
  );
}

function EditRow({
  row,
  ventaId,
  onCancel,
  onSaved,
  requireReconfirmation,
}: {
  row: MantenimientoDetalleRow;
  ventaId: string;
  onCancel: () => void;
  onSaved: (resetConfirmacion: boolean) => void;
  /** When true the venta is already nroDespachoConfirmado — saving
   *  this edit will reset that flag, so we route through a
   *  confirmation dialog before firing the patch. */
  requireReconfirmation: boolean;
}) {
  const [nroSerie, setNroSerie] = useState(row.nroSerie);
  const [nroDespacho, setNroDespacho] = useState(row.nroDespacho);
  // Two-stage submit: clicking Guardar opens the warning when
  // re-confirmation will be needed; the dialog's CTA then runs the
  // mutation. Skipped when the venta isn't confirmed yet.
  const [confirmReconfirmOpen, setConfirmReconfirmOpen] = useState(false);
  useEffect(() => {
    setNroSerie(row.nroSerie);
    setNroDespacho(row.nroDespacho);
  }, [row.id, row.nroSerie, row.nroDespacho]);

  const dirty =
    nroSerie.trim() !== row.nroSerie || nroDespacho.trim() !== row.nroDespacho;

  const mutation = useMutation({
    mutationFn: () =>
      updateDetalleNumeros(ventaId, row.id, {
        nroSerie: nroSerie.trim(),
        nroDespacho: nroDespacho.trim(),
      }),
    onSuccess: (res) => {
      toast.success(
        res.resetConfirmacion
          ? 'Números actualizados — confirmá nuevamente el N° Despacho'
          : 'Números actualizados',
      );
      setConfirmReconfirmOpen(false);
      onSaved(res.resetConfirmacion);
    },
    onError: (err) => {
      setConfirmReconfirmOpen(false);
      toast.error('No se pudo guardar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    },
  });

  const handleSaveClick = () => {
    if (mutation.isPending) return;
    if (!dirty) {
      // Nothing changed → just close the editor, no patch needed.
      onCancel();
      return;
    }
    if (requireReconfirmation) {
      setConfirmReconfirmOpen(true);
      return;
    }
    mutation.mutate();
  };

  return (
    <>
      <tr className="bg-muted/20 align-top">
        <Td className="font-semibold text-foreground">{row.articulo}</Td>
        <Td>
          <Label className="sr-only" htmlFor={`serie-${row.id}`}>
            Num. de Serie
          </Label>
          <Input
            id={`serie-${row.id}`}
            placeholder="Num. de Serie"
            value={nroSerie}
            onChange={(e) => setNroSerie(e.target.value)}
            disabled={mutation.isPending}
            maxLength={120}
          />
        </Td>
        <Td>
          <Label className="sr-only" htmlFor={`despacho-${row.id}`}>
            Num. de Despacho
          </Label>
          <Input
            id={`despacho-${row.id}`}
            placeholder="Num. de Despacho"
            value={nroDespacho}
            onChange={(e) => setNroDespacho(e.target.value)}
            disabled={mutation.isPending}
            maxLength={120}
          />
        </Td>
        <Td>
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={onCancel}
              disabled={mutation.isPending}
              aria-label="Cancelar edición"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <Button
              type="button"
              size="sm"
              onClick={handleSaveClick}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                'Guardar'
              )}
            </Button>
          </div>
        </Td>
      </tr>

      <Dialog
        open={confirmReconfirmOpen}
        onOpenChange={(o) => !o && !mutation.isPending && setConfirmReconfirmOpen(false)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
              Re-confirmación requerida
            </DialogTitle>
            <DialogDescription>
              El N° de Despacho ya fue confirmado para esta venta.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <p className="font-medium">Si guardás los cambios:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs leading-relaxed">
                <li>Se actualizará el Nº de Serie / Despacho de esta línea.</li>
                <li>Se reiniciará la confirmación del N° de Despacho de la venta.</li>
                <li>Vas a tener que volver a presionar <strong>Confirmar N° Despacho</strong> antes de Realizar Despacho.</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmReconfirmOpen(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                'Guardar y re-confirmar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <th className={cn('px-3 py-2 font-semibold', className)}>{children}</th>;
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn('px-3 py-2 align-middle', className)}>{children}</td>;
}
